/**
 * Camada 1c · a tela que o chassi serve em `/mensagens`.
 *
 * ---------------------------------------------------------------------------
 * O DEFEITO QUE ISTO FECHA, medido em 06/09/2026
 * ---------------------------------------------------------------------------
 * A suite de conformidade de tela acusou, nas TRES larguras, em `apps/_template`:
 *
 *   "o sino aceita o clique e a tela nao muda. O chassi liga o sino a /mensagens, entao ou a
 *    ligacao se perdeu, ou o produto nao atende a rota"
 *
 * A ligacao nao se perdeu: `Chassi.tsx` sempre navegou para `/mensagens`. O que faltava era
 * ALGUEM ATENDER. O molde renderiza `children` para qualquer rota, entao o sino levava a
 * pessoa para um lugar que desenhava exatamente a mesma tela de onde ela veio.
 *
 * E A MESMA FAMILIA DO `?` QUE ABRIA NADA, e ela ja custou uma correcao aqui: botao da
 * plataforma que a pessoa aperta e nao responde. A diferenca e que o `?` nao abria gaveta
 * nenhuma, e este abre uma rota que ninguem serve.
 *
 * ---------------------------------------------------------------------------
 * POR QUE O PLANO `control-plane` NAO E ATENDIDO AQUI, e a ausencia e deliberada
 * ---------------------------------------------------------------------------
 * A tela de mensagens do operador NAO E a mesma dos produtos, e nao por gosto: ela atravessa
 * TODOS os clientes, filtra por produto, e mostra de qual sistema cada conversa veio
 * (`apps/admin/src/App.tsx:303` monta `<Mensagens operador={...} />`). Servir aqui um painel
 * de um cliente so seria substituir uma tela rica por uma pobre, e o operador perderia
 * exatamente a visao que o Super Admin existe para dar.
 *
 * ENTAO A REGRA E: plano `tenant`, o chassi serve; plano `control-plane`, o app serve. Isso
 * NAO reabre a porta das 53 linhas, porque o app de plano de controle e UM, e a escolha nao
 * e dele: sai do manifesto, e este arquivo obedece.
 */
import { useMemo } from 'react';
import { PainelConversas, criarTransporteChatHttp } from '@complexity/chat';
import type { MensagensProduto } from '@complexity/product-kit';
import { BASES } from './bases.js';
import type { Credenciais } from './credenciais.js';

/** A segunda rota que o chassi atende sozinho. O app nao a declara e nao a trata. */
export const ROTA_DE_MENSAGENS = '/mensagens';

export interface TelaDeMensagensProps {
  /** De qual produto e a conversa. Sem ele, a operacao recebe conversa sem saber a origem. */
  /** O que o manifesto declara: de que lado esta quem usa, e em que plano a conversa vive. */
  readonly mensagens: MensagensProduto;
  readonly credenciais: Credenciais;
  /*
   * NAO HA `aoFechar`, e a ausencia e decisao dele em 07/09/2026: "nao havera OPCAO
   * PARA FECHAR AS MENSAGENS - RETIRAR ESSE X". Ela existia para o X levar de volta a
   * raiz, e sem o X nao ha para onde voltar: sair e clicar em outro item do menu.
   */
}

/**
 * O QUE ELA DESENHA EM CADA ESTADO, e nenhum deles e tela em branco.
 *
 * AUSENCIA DE DADO SE DIZ, NUNCA SE PREENCHE, e aqui existem tres ausencias diferentes que
 * a pessoa precisa distinguir:
 *   1. sem servico configurado  · e defeito de instalacao, e some ao ligar a variavel
 *   2. sem sessao               · e o caso do molde, que nao tem login nenhum
 *   3. sem cliente resolvido    · e o instante entre entrar e os vinculos chegarem
 *
 * MOSTRAR "Nenhuma mensagem" nos tres seria mentir nos tres: a pessoa concluiria que a caixa
 * esta vazia quando o que houve foi nao ter como perguntar.
 */
export function TelaDeMensagens({
  mensagens,
  credenciais,
}: TelaDeMensagensProps) {
  const { token, tenantId } = credenciais.obter();

  /**
   * O TRANSPORTE E MEMOIZADO PELO MESMO MOTIVO DE `credenciais.ts`: ele e dependencia de
   * efeito do painel, e recria-lo a cada render dispara busca em laco. O laco ja aconteceu
   * nesta casa em 06/09/2026, a 27ms por chamada, e derrubou a Central de Ajuda junto.
   */
  const transporte = useMemo(
    () =>
      criarTransporteChatHttp(BASES.mensagens, mensagens.lado, {
        obter: () => {
          const atual = credenciais.obter();
          return { token: atual.token ?? undefined, tenantId: atual.tenantId ?? undefined };
        },
      }),
    [credenciais, mensagens.lado],
  );

  if (BASES.mensagens === undefined) {
    return (
      <Aviso
        titulo="Mensagens não configuradas"
        detalhe="A variável VITE_CX_API_MESSAGING não está definida neste ambiente, então não há serviço com quem falar. Nenhuma conversa foi perdida: elas não chegaram a ser buscadas."
      />
    );
  }

  if (token === null) {
    return (
      <Aviso
        titulo="Entre para ver suas mensagens"
        detalhe="Esta tela mostra as conversas de quem está com a sessão aberta. Sem sessão, não há de quem mostrar."
      />
    );
  }

  if (tenantId === null) {
    return (
      <Aviso
        titulo="Carregando o seu acesso"
        detalhe="A sessão já está aberta, e o cliente ainda está sendo resolvido. As conversas aparecem assim que ele chegar."
      />
    );
  }

  return (
    <PainelConversas
      tenantId={tenantId}
      transporte={transporte}
      eu={mensagens.lado}
    />
  );
}

/**
 * O aviso dos tres estados de ausencia.
 *
 * ELE E UM COMPONENTE E NAO TRES BLOCOS COPIADOS porque a diferenca entre os tres e o TEXTO,
 * e nao a forma. Copiado, o terceiro envelheceria sozinho no dia em que alguem ajustasse o
 * espacamento dos outros dois.
 */
function Aviso({ titulo, detalhe }: { readonly titulo: string; readonly detalhe: string }) {
  return (
    <div className="flex h-full flex-col items-start justify-center gap-cx-2 p-cx-4">
      <h2 className="text-cx-lg font-semibold text-texto">{titulo}</h2>
      <p className="max-w-prose text-cx-base text-texto-suave">{detalhe}</p>
    </div>
  );
}
